'use client';

/**
 * UserInviteSearch
 *
 * Unified search + invite component used on:
 *   - /u/[userId]/following    (mode="follow")
 *   - Circle add/edit pages    (mode="circle", circleId provided)
 *   - Collection collab panel  (mode="collaborator", collectionId + defaultRole provided)
 *
 * Behaviour:
 *   1. User types a name/username/email → debounced search → user results shown
 *   2. If no user found and input is a valid email → shows email invite form
 *      with optional message (max 255 chars) and, when collectionId is set, a role picker
 *   3. On success for existing users:
 *      - mode="follow": fires sendFollowInvite (+ circle context if provided)
 *      - mode="circle": calls onAdded immediately so parent fires addCircleMember
 *      - mode="collaborator": shows inline role picker per result row, then calls inviteCollaborator
 *   4. On email invite: sends pending invite with full context attached
 *
 * Props:
 *   mode          — 'follow' | 'circle' | 'collaborator'
 *   circleId      — if set, attached to invite so invitee is auto-added on signup
 *   collectionId  — required for mode="collaborator"; used to call inviteCollaborator
 *   defaultRole   — pre-selected role when mode="collaborator" (default: 'editor')
 *   existingIds   — user IDs already in the list (filtered from results)
 *   onAdded       — called with userId + user object after selection/follow
 *   onInvited     — called with email after a successful pending invite
 *   disabled      — disables input while a parent mutation is in-flight
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { searchUsers, sendFollowInvite, inviteCollaborator } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Search, Mail, UserPlus, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CollabRole = 'viewer' | 'editor' | 'admin';

const ROLE_OPTIONS: { value: CollabRole; label: string; description: string }[] = [
  { value: 'viewer', label: 'Viewer', description: 'Can view the list' },
  { value: 'editor', label: 'Editor', description: 'Can add and edit items' },
  { value: 'admin', label: 'Admin', description: 'Can manage members and settings' },
];

interface UserResult {
  _id: string;
  displayName?: string;
  username?: string;
  imageUrl?: string;
}

interface UserInviteSearchProps {
  mode: 'follow' | 'circle' | 'collaborator';
  /** Attached to the invite — invitee auto-joined to this circle on signup */
  circleId?: string;
  /** Required for mode="collaborator" — the collection to invite into */
  collectionId?: string;
  /** Pre-selected role for collaborator mode (default: 'editor') */
  defaultRole?: CollabRole;
  existingIds?: Set<string>;
  onAdded?: (userId: string, user: UserResult) => void;
  onInvited?: (email: string) => void;
  disabled?: boolean;
}

export function UserInviteSearch({
  mode,
  circleId,
  collectionId,
  defaultRole = mode === 'collaborator' ? 'editor' : 'viewer',
  existingIds = new Set(),
  onAdded,
  onInvited,
  disabled = false,
}: UserInviteSearchProps) {
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [role, setRole] = useState<CollabRole>(defaultRole);
  // Per-row role selection for collaborator mode: userId → role
  const [rowRoles, setRowRoles] = useState<Record<string, CollabRole>>({});
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const debouncedQuery = useDebounce(query, 300);
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const isEmail = EMAIL_RE.test(debouncedQuery.trim());

  const { data, isFetching } = useQuery({
    queryKey: ['users', 'search', debouncedQuery, 'open'],
    queryFn: () => searchUsers(debouncedQuery, true),
    enabled: debouncedQuery.length >= 2,
    placeholderData: (prev) => prev,
  });

  const results = (data?.users ?? []).filter(
    (u) => !existingIds.has(u._id) && !justAdded.has(u._id)
  );

  // Show invite form when: valid email AND search returned nothing
  useEffect(() => {
    if (isEmail && !isFetching && results.length === 0 && debouncedQuery.length >= 2) {
      setShowInviteForm(true);
    } else {
      setShowInviteForm(false);
      setMessage('');
    }
  }, [isEmail, isFetching, results.length, debouncedQuery]);

  const followMutation = useMutation({
    mutationFn: (userId: string) =>
      sendFollowInvite({ userId, circleId }),
    onSuccess: (_, userId) => {
      const user = results.find((u) => u._id === userId);
      setJustAdded((prev) => new Set([...prev, userId]));
      setQuery('');
      queryClient.invalidateQueries({ queryKey: ['following'] });
      onAdded?.(userId, user ?? { _id: userId });
      toast.success('Now following');
    },
    onError: (err: Error) => {
      if (err.message.includes('already_following')) {
        toast.info('Already following this user');
      } else {
        toast.error(err.message ?? 'Failed to follow user');
      }
    },
  });

  // Collaborator invite mutation — fires per-row when mode="collaborator"
  const collaboratorMutation = useMutation({
    mutationFn: ({ userId, selectedRole }: { userId: string; selectedRole: CollabRole }) =>
      inviteCollaborator(collectionId!, { type: 'user', userId, role: selectedRole }),
    onSuccess: (_, { userId }) => {
      const user = results.find((u) => u._id === userId);
      setJustAdded((prev) => new Set([...prev, userId]));
      setQuery('');
      queryClient.invalidateQueries({ queryKey: ['collaborators', collectionId] });
      onAdded?.(userId, user ?? { _id: userId });
      toast.success('Invite sent');
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to send invite'),
  });

  function handleSelectUser(user: UserResult) {
    if (mode === 'circle') {
      setJustAdded((prev) => new Set([...prev, user._id]));
      setQuery('');
      onAdded?.(user._id, user);
    } else if (mode === 'collaborator') {
      const selectedRole = rowRoles[user._id] ?? defaultRole;
      collaboratorMutation.mutate({ userId: user._id, selectedRole });
    } else {
      followMutation.mutate(user._id);
    }
  }

  const inviteMutation = useMutation({
    mutationFn: () => {
      const email = debouncedQuery.trim().toLowerCase();
      // In collaborator mode, send via inviteCollaborator (email type) so the
      // pending record lands on the collection, not just as a follow invite.
      if (mode === 'collaborator' && collectionId) {
        return inviteCollaborator(collectionId, {
          type: 'email',
          email,
          role,
        });
      }
      return sendFollowInvite({
        email,
        message: message.trim() || undefined,
        circleId,
      });
    },
    onSuccess: (data: any) => {
      const email = debouncedQuery.trim().toLowerCase();
      if (mode === 'collaborator') {
        queryClient.invalidateQueries({ queryKey: ['collaborators', collectionId] });
        toast.success(`Invite sent to ${email}`);
        onInvited?.(email);
      } else if (data.status === 'invite_accepted' || data.status === 'followed') {
        queryClient.invalidateQueries({ queryKey: ['following'] });
        toast.success('They already have an account — now connected!');
      } else {
        toast.success(`Invite sent to ${email}`);
        onInvited?.(email);
      }
      setQuery('');
      setMessage('');
      setShowInviteForm(false);
    },
    onError: (err: Error) => {
      if (err.message.includes('already_following')) {
        toast.info('Already following this user');
        setQuery('');
        setShowInviteForm(false);
      } else {
        toast.error(err.message ?? 'Failed to send invite');
      }
    },
  });

  const isLoading = followMutation.isPending || inviteMutation.isPending || collaboratorMutation.isPending;

  return (
    <div className="space-y-2">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, username or email…"
          className="pl-9"
          disabled={disabled || isLoading}
          aria-label="Search users or invite by email"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {/* Search results */}
      {debouncedQuery.length >= 2 && !showInviteForm && (
        <ul className="border rounded-xl overflow-hidden divide-y">
          {isFetching && results.length === 0 && (
            <li className="px-4 py-3 text-sm text-muted-foreground">Searching…</li>
          )}
          {!isFetching && results.length === 0 && !isEmail && (
            <li className="px-4 py-3 text-sm text-muted-foreground">No users found.</li>
          )}
          {results.map((user) => {
            const rowRole = rowRoles[user._id] ?? defaultRole;
            return (
              <li key={user._id} className="flex items-center gap-3 px-4 py-3 bg-background">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={user.imageUrl} />
                  <AvatarFallback>{(user.displayName ?? user.username ?? '?')[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.displayName}</p>
                  {user.username && (
                    <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                  )}
                </div>
                {/* Inline role selector — collaborator mode only */}
                {mode === 'collaborator' && (
                  <select
                    value={rowRole}
                    onChange={(e) =>
                      setRowRoles((prev) => ({ ...prev, [user._id]: e.target.value as CollabRole }))
                    }
                    disabled={disabled || isLoading}
                    className="text-xs border rounded-md px-2 py-1 bg-background text-foreground h-8 shrink-0"
                    aria-label="Role"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={disabled || isLoading}
                  onClick={() => handleSelectUser(user)}
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  {mode === 'collaborator' || mode === 'circle' ? 'Invite' : 'Follow'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Email invite form */}
      {showInviteForm && (
        <div className="border rounded-xl p-4 space-y-3 bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4 shrink-0" />
            <span>
              <strong className="text-foreground">{debouncedQuery.trim()}</strong> isn&apos;t on
              Favely yet. Send them an invite
              {circleName(circleId, collectionId, mode)}.
            </span>
          </div>

          {/* Role picker — only when a collection context is provided */}
          {collectionId && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Permission level</p>
              <div className="flex gap-2">
                {ROLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRole(opt.value)}
                    disabled={isLoading}
                    className={cn(
                      'flex-1 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      role === opt.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background hover:bg-muted'
                    )}
                  >
                    <p className="font-medium">{opt.label}</p>
                    <p className={cn('text-xs mt-0.5', role === opt.value ? 'text-background/70' : 'text-muted-foreground')}>
                      {opt.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Personal message — follow mode only (collaborator API doesn't support it) */}
          {mode !== 'collaborator' && (
            <div className="space-y-1">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 255))}
                placeholder="Add a personal message… (optional)"
                rows={3}
                className="resize-none text-sm"
                disabled={isLoading}
              />
              <p
                className={cn(
                  'text-xs text-right',
                  message.length >= 240 ? 'text-orange-500' : 'text-muted-foreground'
                )}
              >
                {message.length}/255
              </p>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQuery('');
                setShowInviteForm(false);
                setMessage('');
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => inviteMutation.mutate()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1" />
              )}
              Send invite
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Returns a context suffix for the invite description line */
function circleName(
  circleId?: string,
  collectionId?: string,
  mode?: string
): string {
  if (circleId) return ' and will be added to this circle when they join';
  if (collectionId) return ' and will be added to this list when they join';
  return '';
}
